import React, { useState, useMemo, useEffect } from 'react';
import '../../pages/Kpi/kpi.css';
import { Chart } from 'react-google-charts';

/**
 * Mapping of database field names to user-friendly display names
 * Used to convert technical field names into readable chart labels
 * Only fields included in this map will be available for chart configuration
 */
const friendlyNameMap: Record<string, string> = {
	bsw: 'BS&W',
	gross_barrels: 'Gross Barrels',
	net_barrels: 'Net Barrels',
	producer: 'Producer',
	load_date: 'Load Date',
	api_gravity: 'API Gravity',
	tank_temp: 'Tank Temp',
	approved_mileage: 'Standard Mileage',
	mileage: 'Charged Mileage',
	observed_gravity: 'Observed Gravity',
	observed_temperature: 'Observed Temperature',
	approved_rate: 'Approved Rate',
    responseTime: 'Response Time (day)',
    corrected_gravity: 'Corrected Gravity',
};

/**
 * Converts database field name to friendly display name
 * @param key - Database field name
 * @returns Friendly name if mapping exists, null otherwise
 */
function convertKeyName(key: string): string | null {
	return friendlyNameMap[key] || null;
}

/**
 * Returns display name for a field, with fallback to original key
 * @param key - Database field name
 * @returns Friendly name or original key if no mapping exists
 */
function displayName(key: string): string {
	return convertKeyName(key) || key;
}

/**
 * Interface for aggregated variable data by customer/producer
 */
interface AvgValueByCustomer {
	customer: string;     // Producer/customer name
	plotted_value: number;      // Volume-weighted average for the selected variable
}

/**
 * Props for the GenericBarChart component
 */
interface ProducerBarChartProps {
	filteredData: any[];
    selected_variable: string;        // Selected variable for charting
	producerSelection: string[]; // Array of selected producer names
}

/**
 * Utility function to round numbers to specified decimal places
 * Uses exponential notation to avoid floating point precision issues
 * @param value - The number to round
 * @param decimals - Number of decimal places
 * @returns Rounded number
 */
function round(value: number, decimals: number): number {
	return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);
}
/**
 * GenericBarChart Component
 * 
 * Renders either:
 * 1. A bar chart showing volume-weighted average for selected variable by producer (when multiple producers selected)
 * 2. A histogram showing selected variable distribution bins (when single producer selected)
 * 
 * Validates that the selected variable exists in the data and contains numeric values.
 * Also displays statistics card with min/max values, total count, and sum of gross barrels.
 * 
 * @param filteredData - Array of data records filtered by month/year and other criteria
 * @param producerSelection - Array of selected producer names for filtering
 * @param selected_variable - The data field to analyze (must exist in filteredData and be numeric)
 */
const GenericBarChart: React.FC<ProducerBarChartProps> = ({ filteredData, producerSelection, selected_variable }) => {
	/**
	 * Memoized calculation of volume-weighted average for selected variable by producer
	 * Formula: Sum(Selected_Variable * Gross_Barrels) / Sum(Gross_Barrels) per producer
	 * Validates that selected_variable exists and contains numeric data
	 * Only recalculates when filteredData or selected_variable changes to prevent infinite loops
	 */
	const avgValueByCustomer: AvgValueByCustomer[] = useMemo(() => {
		if (!filteredData || !Array.isArray(filteredData) || !selected_variable) return [];
		
		// Validate selected_variable exists in the data
		const hasSelectedVariable = filteredData.some(item => 
			item.hasOwnProperty(selected_variable) && item[selected_variable] != null
		);
		
		if (!hasSelectedVariable) {
			console.warn(`Selected variable '${selected_variable}' not found in filtered data`);
			return [];
		}
		
		return Object.values(
			// Reduce to accumulate totals per producer
			filteredData.reduce<{ [customer: string]: { customer: string; total: number; count: number } }>((acc, curr) => {
				const { producer, gross_barrels } = curr;
				const selectedValue = curr[selected_variable];
				
				// Skip records with missing required data or non-numeric values
				if (!producer || 
					gross_barrels == null || 
					selectedValue == null || 
					isNaN(Number(selectedValue)) || 
					isNaN(Number(gross_barrels))) {
					return acc;
				}
				
				// Initialize producer entry if not exists
				if (!acc[producer]) {
					acc[producer] = { customer: producer, total: 0, count: 0 };
				}
				
				// Accumulate weighted totals: Selected_Variable * Volume and total Volume
				acc[producer].total += Number(selectedValue) * Number(gross_barrels);
				acc[producer].count += Number(gross_barrels);
				return acc;
			}, {})
		// Calculate final weighted averages
		).map(({ customer, total, count }) => ({
			customer,
			plotted_value: count > 0 ? Number((total / count).toFixed(3)) : 0,
		}));
	}, [filteredData, selected_variable]);

	/**
	 * Memoized statistics calculation for the current filtered dataset
	 * Calculates min/max values for selected variable, total record count, and sum of gross barrels
	 */
	const stats = useMemo(() => {
		if (!filteredData || filteredData.length === 0 || !selected_variable) {
			return {
				minValue: 0,
				maxValue: 0,
				totalCount: 0,
				sumGrossBarrels: 0,
			};
		}
		
		// Extract and validate numerical values for selected variable
		const selectedValues = filteredData
			.map(item => Number(item[selected_variable]))
			.filter(v => !isNaN(v) && v != null);
		const grossBarrelsValues = filteredData
			.map(item => Number(item.gross_barrels))
			.filter(v => !isNaN(v) && v != null);
		
		return {
			minValue: selectedValues.length > 0 ? Math.min(...selectedValues) : 0,
			maxValue: selectedValues.length > 0 ? Math.max(...selectedValues) : 0,
			totalCount: filteredData.length,
			sumGrossBarrels: grossBarrelsValues.reduce((a, b) => a + b, 0),
		};
	}, [filteredData, selected_variable]);

	/**
	 * Chart data state - format depends on display mode:
	 * - Multi-producer: [["Producer", "BS&W Average"], [producer1, avg1], ...]
	 * - Single producer: [["BS&W Range", "Frequency"], [range1, count1], ...]
	 */
	const [chartData, setChartData] = useState<(string | number | null | object)[][]>([
		["Producer", "BS&W Average", { role: "annotation" }],
		[0, 0, 0],
	]);

	/**
	 * Main effect to update chart data based on producer selection and filtered data
	 * Handles two display modes:
	 * 1. Single producer: Creates histogram with predefined BS&W ranges
	 * 2. Multiple producers: Shows volume-weighted averages per producer
	 */
	useEffect(() => {
		if (!avgValueByCustomer) {
			setChartData([["Producer", "BS&W Average"], [0, 0]]);
			return;
		}
		
		// Single producer mode: Generate histogram data
		if (producerSelection.length === 1) {
			// Extract selected variable values for the selected producer
			const selectedValues = filteredData
				.filter(item => item.producer === producerSelection[0])
				.map(item => Number(item[selected_variable]))
				.filter(v => !isNaN(v) && v != null);
			
			if (selectedValues.length === 0) {
				setChartData([["No Data", "Count", { role: "annotation" }], ["No Data", 0, 0]]);
				return;
			}
			
			// Calculate dynamic bin configuration
			const numBins = Math.max(4, Math.min(15, Math.floor(selectedValues.length / 10))); // Minimum 4 bins, maximum 15 bins
			const minValue = Math.min(...selectedValues);
			const maxValue = Math.max(...selectedValues);
			const averageValue = selectedValues.reduce((sum, val) => sum + val, 0) / selectedValues.length;
			const binWidth = (maxValue - minValue) / numBins;
			
			// Generate dynamic bins with equal spacing
			const bins = Array.from({ length: numBins }, (_, i) => {
				const binMin = minValue + (i * binWidth);
				const binMax = i === numBins - 1 ? maxValue + 0.001 : minValue + ((i + 1) * binWidth); // Slight adjustment for last bin to include max value
				
				// Format labels based on value range for readability
				const formatValue = (val: number) => {
					if (averageValue > 10) {
						// Round to nearest integer for easier reading when average > 10
						if (val >= 1000) return `${Math.round(val / 1000)}k`;
						return Math.round(val).toString();
					} else {
						// Use decimal precision for smaller values
						if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
						if (val >= 1) return val.toFixed(1);
						return val.toFixed(3);
					}
				};
				
				const label = i === numBins - 1 
					? `>=${formatValue(binMin)}` 
					: `${formatValue(binMin)} - ${formatValue(binMax)}`;
				
				return { label, min: binMin, max: binMax };
			});
			
			// Count occurrences in each bin
			const binCounts = bins.map(bin =>
				selectedValues.filter(v => v >= bin.min && v < bin.max).length
			);
			
			// Format data for Google Charts histogram
			const data = [
				[`${displayName(selected_variable)} Bin`, "Count", { role: "annotation" }],
				...bins.map((bin, i) => [bin.label, binCounts[i], binCounts[i]])
			];
			setChartData(data);
		} else {
			// Multiple producer mode: Show volume-weighted averages
			const data = [
				["Producer", `${displayName(selected_variable)} Average`, { role: "annotation" }],
				...avgValueByCustomer.map(item => [item.customer, item.plotted_value, item.plotted_value])
			];
			setChartData(data);
		}
	}, [avgValueByCustomer, producerSelection, selected_variable]);

	/**
	 * Memoized chart configuration options for Google Charts
	 * Adapts title and formatting based on single vs multiple producer selection and selected variable
	 */
	const chartOptions = useMemo(() => ({
		title: producerSelection.length === 1 ? `${displayName(selected_variable)} Histogram for ${producerSelection[0]}` : `${displayName(selected_variable)} Average by Producer`,
		legend: 'top',
		axisTitlesPosition: 'out',
		chartArea: { left: 80, right: 100, top: 40, bottom: 80 },
		bar: { groupWidth: '30%' },
		vAxis: {
			title: producerSelection.length === 1 ? 'Count' : `${displayName(selected_variable)} Average`,
			titleTextStyle: { fontSize: 16, bold: true },
			textStyle: { fontSize: 14, color: '#2973A6' },
			gridlines: { color: '#eee' },
		},
		hAxis: {
			title: producerSelection.length === 1 ? `${displayName(selected_variable)} Bin` : "Producer",
			slantedText: false,
			slantedTextAngle: 45,
			textStyle: { fontSize: 16, bold: true, color: '#222' },
			titleTextStyle: { fontSize: 16, bold: true, color: '#222' },
		},
		annotations: {
			alwaysOutside: true,
			textStyle: { fontSize: 12, bold: true, color: '#000' },
		},
	}), [producerSelection, selected_variable]);

	return (
		<div>
			{/* Statistics Card - Display key metrics for current data set */}
			<div className='kpi_statsCard'>
				<div className='kpi_statItem'>
					<div className='kpi_statTitle'>Min {displayName(selected_variable)}</div>
					<div className='kpi_statValue'>{stats.minValue}</div>
				</div>
				<div className='kpi_statItem'>
					<div className='kpi_statTitle'>Max {displayName(selected_variable)}</div>
					<div className='kpi_statValue'>{stats.maxValue}</div>
				</div>
				<div className='kpi_statItem'>
					<div className='kpi_statTitle'>Total Items</div>
					<div className='kpi_statValue'>{stats.totalCount}</div>
				</div>
				<div className='kpi_statItem'>
					<div className='kpi_statTitle'>Sum Gross Barrels</div>
					<div className='kpi_statValue'>{round(stats.sumGrossBarrels, 2)}</div>
				</div>
			</div>
			
			{/* Main Chart Display */}
			<div className='kpi_chartWrapper'>
				<Chart
					chartType='ColumnChart'
					width='100%'
					height='35rem'
					data={chartData}
					options={chartOptions}
				/>
			</div>
			
			{/* Calculation Method Information */}
            <div className='kpi_subtext'>
                {displayName(selected_variable)} chart calculated with volume weighted averages
            </div>
		</div>
	);
};

export default GenericBarChart;
